import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import Countdown from "react-countdown";
import { toast } from "react-toastify";
import Axios from "../../../Axios";
import { useAppContext } from "../../../Context/AppContext";
import { Urls } from "../../../Urls";
import { decryptResponse } from "../../RateCard/utils";
import { timerRenderer } from "./AuctionsTable/AuctionsTable";
import styles from "./FreightBreakups.module.css";
import { useMixPanelForTracking } from "../../../shared_utilities/trackMixPanel";

const FreightBreakups = ({ auctionID }) => {
  const { transporterInfo } = useAppContext();
  const {TrackMixPanel} = useMixPanelForTracking()
  const [auctionData, setAuctionData] = useState(null);
  const [freightData, setFreightData] = useState([]);
  const [laneRates, setLaneRates] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState("Pending");
  const [submissionWindowStatus, setSubmissionWindowStatus] =
    useState("pending");

  useEffect(() => {
    if (auctionID && transporterInfo?.transporter_id) {
      fetchAuctionLanes();
    }
  }, [auctionID, transporterInfo.transporter_id]);

  const fetchAuctionLanes = () => {
    setLoading(true);
    Axios.get(
      `${Urls.auction}?auction_id=${auctionID}&transporter_id=${transporterInfo.transporter_id}&lot_auction_freight_breakup=true`
    )
      .then((res) => {
        const data = res?.data;
        setAuctionData(data);
        TrackMixPanel({
          Definition:"user has opened the freight breakup pop-up.",
          ModuleName:"tpmp auction",
          ScreenName:"auctions - closed auctions - post auction freight breakup",
          Data:{
            "AuctionId":auctionID,
            "AuctionDescription":data?.auction_description,
            "ProcurementName":data?.procurement_name,
            "LotNo":data?.lot_no,
            "FreightBreakupSubmissionStatus":data?.freight_breakup_submission_status,
            "Lanes":data?.lane_freight_breakup?.length || 0
          }
        })
        if (data?.lot_wise_transporter_submission_status === true) {
          setSubmissionStatus("Submitted");
        }
        if (data?.lane_freight_breakup?.length > 0) {
          setFreightData(data.lane_freight_breakup);
          const initialRates = {};
          data.lane_freight_breakup.forEach((lane) => {
            initialRates[lane.lane_id] = lane?.freight_rate ?? "";
          });
          setLaneRates(initialRates);
        }
        setSubmissionWindowStatus(
          data?.freight_breakup_submission_status ?? "pending"
        );
        setLoading(false);
      })
      .catch((err) => {
        console.error("API fetch error", err);
        setLoading(false);
      });
  };

  const isNumericRateValid = (val) => {
    const num = Number(val);
    return !isNaN(num) && num > 0 && /^\d+(\.\d{1,2})?$/.test(val);
  };

  const validateLaneRate = (laneId, value) => {
    const trimmed = value?.trim();
    return isNumericRateValid(trimmed)
      ? ""
      : "Enter a valid rate greater than 0";
  };

  const handleRateChange = (laneId, value) => {
    setLaneRates((prev) => ({ ...prev, [laneId]: value }));
    const error = validateLaneRate(laneId, value);
    setErrors((prev) => ({
      ...prev,
      ...(error ? { [laneId]: error } : { [laneId]: undefined }),
    }));
  };

  const calculateLaneSpend = (laneId, volume) => {
    const rate = parseFloat(laneRates[laneId]);
    return isNaN(rate) ? 0 : rate * volume;
  };

  const calculateTotalSpend = () => {
    return freightData.reduce((sum, lane) => {
      return sum + calculateLaneSpend(lane.lane_id, lane.volume);
    }, 0);
  };

  const isFormValid = useMemo(() => {
    if (!freightData?.length) return false;
    return freightData.every((lane) => {
      const val = laneRates[lane.lane_id];
      return isNumericRateValid(val) && !errors[lane.lane_id];
    });
  }, [freightData, laneRates, errors]);

  const latestValidBid = useMemo(() => {
    if (!auctionData?.bid_list?.length) return null;
    return (
      auctionData.bid_list
        .filter((bid) => bid.is_valid)
        .sort(
          (a, b) => new Date(b.bid_timestamp) - new Date(a.bid_timestamp)
        )[0] || null
    );
  }, [auctionData?.bid_list]);

  const getFormattedDate = (timestamp) => {
    return timestamp
      ? format(new Date(timestamp), "dd-MM-yyyy HH:mm:ss")
      : "N/A";
  };

  const SubmitRates = async () => {
    setLoading(true);

    TrackMixPanel({
      Definition:"user has clicked the submit quotes button.",
      ModuleName:"tpmp auction",
      ScreenName:"auctions - closed auctions - post auction freight breakup",
      Data:{
        "AuctionId":auctionID,
        "AuctionDescription":auctionData?.auction_description,
        "ProcurementName":auctionData?.procurement_name,
        "LotNo":auctionData?.lot_no,
        "FreightBreakupSubmissionStatus":auctionData?.freight_breakup_submission_status,
        "Lanes":auctionData?.lane_freight_breakup?.length || 0
      }
    })

    const payload = {
      auction_id: auctionData?.auction_id,
      transporter_id: transporterInfo?.transporter_id,
      procurement_id: auctionData?.procurement_id,
      transporter_name: transporterInfo?.transporter_name,
      allowed_deviation: auctionData?.allowed_deviation ?? 0,
      lot_no: auctionData?.lot_no,
      lane_freight_breakup: freightData.map((lane) => ({
        lane_id: lane?.lane_id,
        freight: parseFloat(laneRates[lane?.lane_id]),
        volume: lane?.volume ? parseInt(lane?.volume) : 1,
      })),
    };

    Axios.post(
      `${Urls.auction}?submit_freight_breakup=true&transporter_id=${transporterInfo.transporter_id}`,
      payload
    )
      .then((response) => {
        const res = {
          data: decryptResponse(response?.data),
        };
        if (res?.data?.error) {
          toast.error(res.data.error);
          setLoading(false);
          return;
        }
        fetchAuctionLanes();
        toast.success("Rates submitted successfully!");
        setLoading(false);
      })
      .catch((err) => {
        console.error("Submission error", err);
        setLoading(false);
      });
  };

  const renderBidHistoryRow = () => {
    if (!latestValidBid) {
      return (
        <tr>
          <td colSpan="6" style={{ textAlign: "center", color: "#888" }}>
            No valid bids available
          </td>
        </tr>
      );
    }

    return (
      <tr>
        <td>{latestValidBid.name || "N/A"}</td>
        <td>{latestValidBid.contact_number || "N/A"}</td>
        <td>{getFormattedDate(latestValidBid.bid_timestamp)}</td>
        <td>{latestValidBid.bid_amount?.toLocaleString()}</td>
        <td className={styles.accepted}>Accepted</td>
        <td>{latestValidBid.ip || "N/A"}</td>
      </tr>
    );
  };

  if (auctionData?.bid_list?.length === 0) {
    return (
      <div className="w-100 d-flex align-items-center justify-content-center">
        You did not bid in this Auction.
      </div>
    );
  }

  return (
    <div className={styles.auctionContainer}>
      {loading ? (
        <div
          style={{ height: "470px" }}
          className="d-flex justify-content-center align-items-center"
        >
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.topRow}>
            <span className="w-25">
              <strong>Auction ID :</strong> {auctionData?.display_id || ""}
            </span>
            <span className="w-75">
              <strong>Auction Description :</strong>{" "}
              {auctionData?.auction_description || ""}
            </span>
          </div>

          {freightData?.length > 0 && (
            <>
              <div className={styles.lotBox}>
                <div>
                  <strong>Lot No. :</strong> {auctionData?.lot_no || ""}
                </div>
                <div className={styles.divider}></div>
                <div>
                  {/* <strong>Freight Breakup Submission Status :</strong>{" "} */}
                  <span
                    style={{
                      color: submissionStatus === "Pending" ? "red" : "green",
                    }}
                  >
                    {submissionStatus === "Pending"
                      ? "Freight Breakup not submitted"
                      : "Freight Breakup Submitted"}
                  </span>
                </div>
                <div className={styles.divider}></div>
                {submissionWindowStatus === "active" ? (
                  <div className="d-flex">
                    <strong>Submission Window Closes in :</strong>
                    <div className="mx-2">
                      <Countdown
                        date={
                          auctionData?.freight_breakup_submission_timestamp ??
                          new Date()
                        }
                        onComplete={fetchAuctionLanes}
                        renderer={timerRenderer}
                      />
                    </div>
                  </div>
                ) : submissionWindowStatus === "pending" ? (
                  <div className="text-info">
                    <i className="fa fa-info-circle mx-1"></i>
                    Submission window not started
                  </div>
                ) : (
                  <div className="text-danger">
                    <i className="fa fa-info-circle mx-1"></i>
                    Submission window closed
                  </div>
                )}
              </div>

              <div className={styles.sectionHeader}>
                <h5 className={styles.freightTitle}>
                  Lane Wise Freight Breakups:
                </h5>
                <div className={styles.rankBox}>
                  Your Final Rank :{" "}
                  <span className={styles.rankBadge}>
                    {auctionData?.rank ? `L${auctionData.rank}` : "-"}
                  </span>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.freightTable}>
                  <thead>
                    <tr>
                      <th>Lane Description</th>
                      <th>Truck Type</th>
                      <th>Units</th>
                      <th>Lane Rate</th>
                      <th>Total Lane Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freightData.map((lane) => (
                      <tr key={lane.lane_id}>
                        <td>
                          {lane.origin_name} ({lane.origin_state}) →{" "}
                          {lane.destination_name} ({lane.destination_state})
                        </td>
                        <td>{lane.truck_type}</td>
                        <td>{lane.volume}</td>
                        <td>
                          <input
                            type="number"
                            value={laneRates[lane.lane_id] || ""}
                            onChange={(e) =>
                              handleRateChange(lane.lane_id, e.target.value)
                            }
                          />
                          {errors[lane.lane_id] && (
                            <div style={{ color: "red", fontSize: "12px" }}>
                              {errors[lane.lane_id]}
                            </div>
                          )}
                        </td>
                        <td>
                          {calculateLaneSpend(
                            lane.lane_id,
                            lane.volume
                          ).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.sectionHeader2}>
                <button
                  className={`${styles.submitBtn} ${styles.rankBox}`}
                  disabled={
                    !isFormValid ||
                    submissionWindowStatus !== "active" ||
                    auctionData?.bid_list?.length < 1
                  }
                  style={{
                    cursor: !isFormValid ? "not-allowed" : "pointer",
                    opacity: !isFormValid ? 0.6 : 1,
                  }}
                  onClick={SubmitRates}
                >
                  Submit Quotes
                </button>
                <div className={styles.rankText}>
                  <span style={{ fontSize: 16 }}>Total Spend: </span>
                  <strong style={{ fontSize: 18 }}>
                    {calculateTotalSpend().toLocaleString()}
                  </strong>
                </div>
              </div>
            </>
          )}

          <div className={styles.bidHistoryBox}>
            <h6 className={styles.bidHeader}>Bid History:</h6>
            <table className={styles.bidTable}>
              <thead className="border-bottom border-top">
                <tr>
                  <th>LSP POC Name</th>
                  <th>LSP POC Number</th>
                  <th>Date and Time</th>
                  <th>Bid Amount</th>
                  <th>Bid Status</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>{renderBidHistoryRow()}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default FreightBreakups;
